import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, UserCircle2, ClipboardCheck,
    Trophy, BarChart3, Wallet, Megaphone, Settings, LogOut,
    Calendar, Newspaper, ChevronDown, FileCheck2, PanelsTopLeft
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { validSeasonSelection } from "../../utils/seasonSelection";

const NAV = [
    { to: "/admin", icon: LayoutDashboard, label: "Tableau de bord", end: true, tid: "nav-dashboard" },
    { to: "/admin/seasons", icon: Calendar, label: "Saisons", tid: "nav-seasons" },
    { to: "/admin/teams", icon: Trophy, label: "Équipes", tid: "nav-teams" },
    { to: "/admin/players", icon: UserCircle2, label: "Joueurs", tid: "nav-players" },
    { to: "/admin/parents", icon: Users, label: "Parents", tid: "nav-parents" },
    { to: "/admin/registrations", icon: FileCheck2, label: "Inscriptions", tid: "nav-registrations" },
    { to: "/admin/attendance", icon: ClipboardCheck, label: "Présences", tid: "nav-attendance" },
    { to: "/admin/matches", icon: Trophy, label: "Matchs", tid: "nav-matches" },
    { to: "/admin/stats", icon: BarChart3, label: "Statistiques", tid: "nav-stats" },
    { to: "/admin/payments", icon: Wallet, label: "Cotisations", tid: "nav-payments" },
    { to: "/admin/announcements", icon: Megaphone, label: "Annonces", tid: "nav-announcements" },
    { to: "/admin/news", icon: Newspaper, label: "Actualités", tid: "nav-news" },
    { to: "/admin/homepage", icon: PanelsTopLeft, label: "Page d’accueil", tid: "nav-homepage" },
    { to: "/admin/members", icon: Settings, label: "Membres", tid: "nav-members" },
];

export function navigationForRole(role, isSuperAdmin = false) {
    return NAV.filter((item) => {
        if (role === "coach") {
            return [
                "/admin",
                "/admin/players",
                "/admin/parents",
                "/admin/attendance",
                "/admin/matches",
                "/admin/stats",
                "/admin/announcements",
            ].includes(item.to);
        }
        if (["club_admin", "super_admin"].includes(role) || isSuperAdmin) return true;
        return !["/admin/registrations", "/admin/homepage"].includes(item.to);
    });
}

export default function Sidebar({ mobile = false, onNavigate }) {
    const { user, logout, activeClubId, setActiveClubId, activeSeasonId, setActiveSeasonId } = useAuth();
    const [clubs, setClubs] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/clubs").then((r) => setClubs(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!activeClubId) {
            setSeasons([]);
            return undefined;
        }
        let cancelled = false;
        api.get("/seasons", { params: { club_id: activeClubId } }).then((r) => {
            if (cancelled) return;
            setSeasons(r.data);
            const validSelection = validSeasonSelection(r.data, activeSeasonId);
            if (validSelection !== activeSeasonId) {
                setActiveSeasonId(validSelection);
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [activeClubId, activeSeasonId, setActiveSeasonId]);

    const activeClub = clubs.find((c) => c.id === activeClubId);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };
    const role = user?.roles?.find((r) => r.club_id === activeClubId)?.role;
    const visibleNav = navigationForRole(role, user?.roles?.some((r) => r.role === "super_admin"));

    return (
        <aside
            data-testid={mobile ? "admin-mobile-navigation" : "admin-sidebar"}
            className={`${mobile ? "flex h-full w-full" : "hidden lg:flex min-h-screen sticky top-0 w-64"} shrink-0 flex-col bg-slate-900 text-slate-300`}
        >
            <div className="px-6 py-6 border-b border-slate-800">
                <div className="flex items-center gap-2">
                    <img
                        src="https://res.cloudinary.com/dswtir2fh/image/upload/logo_hampwx"
                        alt="ASSMBB"
                        className="w-10 h-10 object-contain bg-white rounded-lg p-1"
                    />

                    <div>
                        <div className="font-display font-bold text-white leading-tight">
                            Mada Sport Club Manager
                        </div>

                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                            ASSMBB
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 py-4 border-b border-slate-800 space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Club actif</label>
                <div className="relative">
                    <select
                        data-testid="sidebar-club-select"
                        value={activeClubId}
                        onChange={(e) => setActiveClubId(e.target.value)}
                        className="w-full appearance-none bg-slate-800 text-white text-sm rounded-md px-3 py-2 pr-8 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                        {clubs.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 pointer-events-none" />
                </div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Saison</label>
                <div className="relative">
                    <select
                        data-testid="sidebar-season-select"
                        value={activeSeasonId}
                        onChange={(e) => setActiveSeasonId(e.target.value)}
                        className="w-full appearance-none bg-slate-800 text-white text-sm rounded-md px-3 py-2 pr-8 border border-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                        {seasons.map((s) => (<option key={s.id} value={s.id}>{s.name}{s.is_active ? " (active)" : ""}</option>))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 pointer-events-none" />
                </div>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                {visibleNav.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        data-testid={item.tid}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                isActive ? "bg-orange-500 text-white" : "hover:bg-slate-800 hover:text-white"
                            }`
                        }
                    >
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                    </NavLink>
                ))}
            </nav>

            <div className="border-t border-slate-800 p-4">
                <div className="text-xs text-slate-400 mb-2 truncate">{user?.full_name}</div>
                <div className="text-[10px] text-slate-500 mb-3 truncate">{user?.email}</div>
                <button
                    data-testid="logout-button"
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium transition-colors"
                >
                    <LogOut className="w-4 h-4" /> Déconnexion
                </button>
            </div>
        </aside>
    );
}
